import { omit } from 'lodash-es';

import type { GlobalAdminFilters, TransformedGlobalAdminFilters } from 'components/ui/filters/interfaces/globalFilter';

import { GlobalAdminFilterProperties } from 'components/ui/filters/interfaces/globalFilter';
import { RoutePath } from 'enums/routePath';

export const acceptableEmptyParams = ['type'];

export const removePaginationParams = searchParameters => omit(searchParameters, ['first', 'after', 'last', 'before']);

export const GLOBAL_FILTER_DISABLED_ROUTES = [RoutePath.ROOFTOPS, RoutePath.WEBSITES];

export const transformGlobalAdminFilters = (data?: GlobalAdminFilters): TransformedGlobalAdminFilters | undefined => {
  const transformedFilters = {
    groupId: data?.[GlobalAdminFilterProperties.GROUPS]
      ? String(data[GlobalAdminFilterProperties.GROUPS].id)
      : undefined,
    rooftopId: data?.[GlobalAdminFilterProperties.ROOFTOPS]
      ? data[GlobalAdminFilterProperties.ROOFTOPS].map(rooftop => String(rooftop.id))
      : undefined,
  };

  if (!transformedFilters.rooftopId && !transformedFilters.groupId) {
    return undefined;
  }
  if (!transformedFilters.rooftopId) {
    delete transformedFilters.rooftopId;
  }
  if (!transformedFilters.groupId) {
    delete transformedFilters.groupId;
  }

  return transformedFilters;
};

export const getInitialLocalSearchParams = ({
  globalSearchParams,
  searchParams,
}: {
  globalSearchParams?: GlobalAdminFilters;
  searchParams?: Record<string, unknown>;
}) => {
  const searchParametersCopy = { ...searchParams };

  if (globalSearchParams?.rooftops) {
    delete searchParametersCopy.rooftopId;
  }

  if (globalSearchParams?.groups) {
    delete searchParametersCopy.groupId;
  }

  return searchParametersCopy;
};

export const mergeGlobalAdminAndLocalFilters = ({
  globalAdminFilters,
  localSearchFilters,
  requestedRoute,
}: {
  globalAdminFilters?: TransformedGlobalAdminFilters;
  localSearchFilters?: Record<string, any>;
  requestedRoute?: RoutePath;
}): Record<string, any> => {
  if (requestedRoute && !isGlobalFiltersAvailableForRoute(requestedRoute)) {
    return localSearchFilters || {};
  }

  const mergedFilters =
    globalAdminFilters?.rooftopId || globalAdminFilters?.groupId
      ? {
          ...localSearchFilters,
          groupId: globalAdminFilters?.groupId,
          rooftopId: globalAdminFilters?.rooftopId?.length ? globalAdminFilters?.rooftopId : undefined,
        }
      : {
          ...localSearchFilters,
        };

  // If there is only one item in an array then the filters expect this to be a single string value
  if (mergedFilters.rooftopId?.length === 1) {
    mergedFilters.rooftopId = mergedFilters.rooftopId[0];
  }

  if (!mergedFilters.rooftopId) {
    delete mergedFilters.rooftopId;
  }
  if (!mergedFilters.groupId) {
    delete mergedFilters.groupId;
  }

  return mergedFilters;
};

export const isFilterPanelEnabledGivenActiveGlobalFilters = ({
  globalAdminFilters,
  searchParamName,
}: {
  globalAdminFilters?: GlobalAdminFilters;
  searchParamName: string;
}): boolean => {
  if (['groupId', 'rooftopId'].includes(searchParamName)) {
    return (
      !globalAdminFilters?.[GlobalAdminFilterProperties.ROOFTOPS]?.length &&
      !globalAdminFilters?.[GlobalAdminFilterProperties.GROUPS]
    );
  }

  return true;
};

export const isGlobalFiltersEmpty = (filters?: GlobalAdminFilters): boolean =>
  !filters?.[GlobalAdminFilterProperties.ROOFTOPS]?.length && !filters?.[GlobalAdminFilterProperties.GROUPS];

export const isGlobalFiltersAvailableForRoute = (route: RoutePath): boolean =>
  !GLOBAL_FILTER_DISABLED_ROUTES.includes(route);
